/**
 * Web Component Registration for FGA Manager
 *
 * This module registers the FgaManagerComponent as a custom element
 * using Angular Elements and NgModule with platformBrowserDynamic.
 *
 * @packageDocumentation
 */

import { Injector, NgModule, DoBootstrap } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule, provideAnimations } from '@angular/platform-browser/animations';
import { HttpClientModule, provideHttpClient, withInterceptors } from '@angular/common/http';
import { createCustomElement } from '@angular/elements';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';

import { provideNgKitTheme } from '@wtw/angularkit/ngkit-theme';

import { FgaManagerComponent } from './fga-manager.component';
import { authInterceptor } from './interceptors/auth.interceptor';
import type { LogLevel, LogEvent } from './models/log-event.model';
import { FGA_MANAGER_VERSION } from './version';

/**
 * Dispatch a log event to the host application.
 *
 * This function emits a `fga-manager-log` CustomEvent on the window object,
 * allowing host applications to capture and handle component lifecycle logs
 * without polluting the console directly.
 *
 * @param level - The log level ('debug' | 'info' | 'warn' | 'error')
 * @param message - The log message
 * @param data - Optional additional data to include
 *
 * @example
 * ```typescript
 * // Host application listens for log events
 * window.addEventListener('fga-manager-log', (event) => {
 *   const { level, message, source, timestamp, data } = event.detail;
 *   console.log(`[${level}] ${message}`, data);
 * });
 * ```
 */
function dispatchLogEvent(level: LogLevel, message: string, data?: unknown): void {
  const event: LogEvent = {
    level,
    message,
    source: 'FgaManagerElement',
    timestamp: Date.now(),
    data,
  };
  window.dispatchEvent(new CustomEvent('fga-manager-log', { detail: event }));
}

/**
 * NgModule for bootstrapping the FGA Manager web component.
 *
 * Uses DoBootstrap for manual bootstrapping as web components
 * do not have a root selector like traditional Angular apps.
 */
@NgModule({
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    FgaManagerComponent
  ],
  providers: [
    provideAnimations(),
    provideHttpClient(withInterceptors([authInterceptor])),
    provideNgKitTheme(), // Provides PRIME_NG_CONFIG, NgKitTheme preset, and CSS layer ordering — supersedes providePrimeNG()
  ]
})
export class FgaManagerModule implements DoBootstrap {
  constructor(private readonly injector: Injector) {}

  /**
   * Manual bootstrap hook - Angular calls this instead of bootstrapping a component.
   * We use this to register our custom element.
   */
  ngDoBootstrap(): void {
    // Check if already defined to support HMR and multiple registrations
    if (!customElements.get('fga-manager')) {
      const FgaManagerElement = createCustomElement(FgaManagerComponent, {
        injector: this.injector
      });
      customElements.define('fga-manager', FgaManagerElement);
    }
  }
}

/**
 * Registers the fga-manager web component.
 *
 * Call this function to initialize Angular and register the custom element.
 * The element will be available as `<fga-manager>` after registration.
 *
 * @returns Promise that resolves when registration is complete
 *
 * @example
 * ```html
 * <script type="module">
 *   import { registerFgaManagerElement } from './fga-manager-bundle.js';
 *
 *   // Listen for component lifecycle logs before registration
 *   window.addEventListener('fga-manager-log', (event) => {
 *     const { level, message, source, data } = event.detail;
 *     console[level](`[${source}] ${message}`, data ?? '');
 *   });
 *
 *   // Setup token provider before registration
 *   window.addEventListener('wc-token-request', async (event) => {
 *     const token = await getAuthToken();
 *     window.dispatchEvent(new CustomEvent('wc-token-response', {
 *       detail: { token }
 *     }));
 *   });
 *
 *   // Register the web component
 *   await registerFgaManagerElement();
 *
 *   // Now safe to use <fga-manager> in DOM
 * </script>
 *
 * <fga-manager
 *   organisation-id="org_demo"
 *   fga-api-base="http://localhost:3000">
 * </fga-manager>
 * ```
 */
export async function registerFgaManagerElement(): Promise<void> {
  // Check if already registered
  if (customElements.get('fga-manager')) {
    dispatchLogEvent('warn', 'Custom element already registered, skipping.');
    return;
  }

  try {
    await platformBrowserDynamic().bootstrapModule(FgaManagerModule, {
      ngZone: 'zone.js'
    });
    dispatchLogEvent('info', `FGA Manager v${FGA_MANAGER_VERSION} registered.`);
  } catch (err) {
    dispatchLogEvent('error', 'Failed to register web component', { error: err });
    throw err;
  }
}

/**
 * Type augmentation for the global CustomElementRegistry.
 * Provides TypeScript intellisense for the fga-manager element.
 */
declare global {
  interface HTMLElementTagNameMap {
    'fga-manager': HTMLElement & {
      organisationId: string;
      fgaApiBase: string;
      auth0ManagementApiBase?: string;
      readonly: boolean;
    };
  }
}
